import React, { useState } from 'react';
import { CheckIcon, ArrowRightIcon, HelpCircleIcon } from 'lucide-react';
const NewPricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const plans = [{
    name: 'Starter',
    description: 'For startups and teams introducing trusted automation',
    price: 39,
    period: 'month',
    note: 'Billed monthly',
    cta: 'Get started',
    ctaStyle: 'secondary',
    ctaLink: 'https://chat.shopilots.com/signup?product=shopilots',
    features: [{
      text: '1 AI agent',
      highlight: true,
      note: 'add more for $20/mo'
    }, {
      text: '1 team member',
      highlight: true,
      note: 'add more for $20/mo'
    }, {
      text: '250 conversations/month',
      highlight: true,
      note: '~10K messages'
    }, {
      text: 'Channels: Web, Mobile Apps, WhatsApp, Telegram, Instagram'
    }, {
      text: 'AI Email Automation'
    }, {
      text: '3-month chat history'
    }],
    additionalNote: 'Additional conversations: $15/1,000'
  }, {
    name: 'Professional',
    description: 'For growing teams ready to scale automation with control',
    price: 99,
    period: 'month',
    note: 'Billed monthly',
    popular: true,
    cta: 'Get started',
    ctaStyle: 'primary',
    ctaLink: 'https://chat.shopilots.com/signup?product=shopilots',
    features: [{
      text: '2 AI agents',
      highlight: true,
      note: 'add extra for $30/mo'
    }, {
      text: '3 team members',
      highlight: true,
      note: 'add extra for $30/mo'
    }, {
      text: '2,000 conversations/month',
      highlight: true,
      note: '~80K messages'
    }, {
      text: 'Zendesk, Freshdesk & Google Analytics integration'
    }, {
      text: 'Phone Call AI',
      highlight: true
    }, {
      text: '1-year chat history'
    }, {
      text: 'AI Summary, Auto-resolve tickets, Agent Assist'
    }, {
      text: 'Campaign Messaging'
    }, {
      text: 'API & Webhooks access'
    }, {
      text: 'Priority support',
      highlight: true
    }],
    additionalNote: 'Additional conversations: $10/1,000'
  }, {
    name: 'Enterprise',
    description: 'For large organizations running AI as a core support layer',
    price: 199,
    period: 'month',
    note: 'Or contact sales for custom pricing',
    cta: 'Talk to Sales',
    ctaStyle: 'outline',
    ctaLink: 'mailto:info@shopilots.com?subject=Enterprise%20Plan%20Inquiry',
    features: [{
      text: 'Custom number',
      highlight: true,
      note: 'of AI agents & seats'
    }, {
      text: 'Discounted conversation/message pricing'
    }, {
      text: 'Salesforce, HubSpot, Zapier integrations'
    }, {
      text: 'Dedicated account manager',
      highlight: true
    }, {
      text: 'SLA-backed support'
    }, {
      text: 'Advanced analytics & insights'
    }, {
      text: 'SSO & identity management'
    }, {
      text: 'Custom features & integrations'
    }, {
      text: 'Remove ShoPilots branding'
    }]
  }];
  const comparisonFeatures = [{
    feature: 'AI agents included',
    starter: '1',
    professional: '2',
    enterprise: 'Custom'
  }, {
    feature: 'Team members',
    starter: '1',
    professional: '3',
    enterprise: 'Custom'
  }, {
    feature: 'Conversations included',
    starter: '250',
    professional: '2,000',
    enterprise: 'Custom'
  }, {
    feature: 'Chat history',
    starter: '3 months',
    professional: '1 year',
    enterprise: 'Custom'
  }, {
    feature: 'Integrations',
    starter: 'Core',
    professional: 'CRM + Analytics',
    enterprise: 'Full ecosystem'
  }, {
    feature: 'Phone Call AI',
    starter: false,
    professional: true,
    enterprise: true
  }, {
    feature: 'Priority support',
    starter: false,
    professional: true,
    enterprise: 'SLA'
  }, {
    feature: 'Governance & Controls',
    starter: 'Basic',
    professional: 'Standard',
    enterprise: 'Advanced'
  }, {
    feature: 'Branding removal',
    starter: false,
    professional: false,
    enterprise: true
  }, {
    feature: 'Dedicated account manager',
    starter: false,
    professional: false,
    enterprise: true
  }];
  const faqs = [{
    question: 'What does "conversation" mean?',
    answer: 'A conversation includes all messages exchanged in a session. If your AI resolves frequent questions, your team saves real time.'
  }, {
    question: 'Can I try before I buy?',
    answer: 'Yes — all plans include a 30-day free trial so you can build, deploy, and observe AI behavior in your own workflows.'
  }, {
    question: 'Can I add more agents or team members?',
    answer: 'Absolutely — both AI agents and human seats can be scaled as your needs evolve.'
  }, {
    question: "What if AI doesn't perform well initially?",
    answer: "ShoPilots' philosophy is to start safe — you always have visibility and human escalation from day one, and you can expand automation responsibly."
  }, {
    question: 'How do I know which plan to start with?',
    answer: "If you're new to AI in support, start with Starter. You can always expand once you see stable results."
  }, {
    question: 'Do I need to automate everything?',
    answer: 'No. Many teams see ROI by automating a small subset of conversations well. Quality over quantity.'
  }, {
    question: 'Can we upgrade later?',
    answer: "Yes — upgrades reflect maturity, not pressure. Move up when you're ready."
  }, {
    question: "What if AI doesn't perform in our use case?",
    answer: "That's exactly why human handoff and visibility are built in from day one. You're never flying blind."
  }];
  return <div className="bg-gray-50" data-id="element-3688">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-white" data-id="element-3689">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50" data-id="element-3690"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-3691">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-id="element-3692">
            Pricing that helps you grow your confidence with AI
          </h1>
          <p className="text-xl text-gray-600 mb-8" data-id="element-3693">
            Start safe, scale selectively, and operate with confidence.
          </p>

          <div className="flex gap-6 justify-center flex-wrap mb-8" data-id="element-3694">
            <div className="flex items-center gap-2 text-gray-600" data-id="element-3695">
              <CheckIcon className="h-5 w-5 text-green-500" data-id="element-3696" />
              <span className="text-sm font-medium" data-id="element-3697">
                30-day free trial on all plans
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600" data-id="element-3698">
              <CheckIcon className="h-5 w-5 text-green-500" data-id="element-3699" />
              <span className="text-sm font-medium" data-id="element-3700">
                No credit card required
              </span>
            </div>
          </div>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-gray-100 p-1.5 rounded-xl" data-id="element-3701">
            <button onClick={() => setBillingPeriod('monthly')} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${billingPeriod === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3702">
              Monthly billing
            </button>
            <button onClick={() => setBillingPeriod('annual')} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${billingPeriod === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3703">
              Annual billing
              <span className="bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full text-xs font-bold" data-id="element-3704">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-3705">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-3706">
          {plans.map((plan, index) => <div key={index} className={`relative bg-white rounded-3xl p-9 flex flex-col ${plan.popular ? 'border-2 border-blue-600 shadow-xl' : 'border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg'} transition-all duration-300`} data-id="element-3707">
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide" data-id="element-3708">
                  Popular
                </div>}

              <div className="mb-6" data-id="element-3709">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3710">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3711">
                  {plan.description}
                </p>
              </div>

              <div className="mb-6" data-id="element-3712">
                <div className="flex items-baseline gap-1" data-id="element-3713">
                  <span className="text-2xl font-semibold text-gray-700" data-id="element-3714">
                    $
                  </span>
                  <span className="text-6xl font-bold text-gray-900" data-id="element-3715">
                    {plan.price}
                  </span>
                  <span className="text-gray-500 font-medium" data-id="element-3716">
                    /{plan.period}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mt-1" data-id="element-3717">{plan.note}</p>
              </div>

              <a href={plan.ctaLink} target={plan.ctaLink.startsWith('http') ? '_blank' : undefined} rel={plan.ctaLink.startsWith('http') ? 'noopener noreferrer' : undefined} className={`w-full py-3.5 px-6 rounded-xl font-semibold text-center transition-all duration-300 mb-7 inline-flex items-center justify-center gap-2 ${plan.ctaStyle === 'primary' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl' : plan.ctaStyle === 'secondary' ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-transparent text-gray-900 border-2 border-gray-300 hover:border-blue-600 hover:text-blue-600'}`} data-id="element-3718">
                {plan.cta}
                {plan.ctaStyle !== 'outline' && <ArrowRightIcon className="h-5 w-5" data-id="element-3719" />}
              </a>

              <div className="flex-grow" data-id="element-3720">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-4" data-id="element-3721">
                  {index === 0 ? "What's included" : 'Everything in ' + (index === 1 ? 'Starter' : 'Professional') + ', plus'}
                </h4>
                <ul className="space-y-3.5" data-id="element-3722">
                  {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-3723">
                      <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-3724" />
                      <div className="flex-1" data-id="element-3725">
                        <span className={`text-sm ${feature.highlight ? 'font-semibold text-gray-900' : 'text-gray-700'}`} data-id="element-3726">
                          {feature.text}
                        </span>
                        {feature.note && <span className="text-sm text-gray-500" data-id="element-3727">
                            {' '}
                            {feature.note}
                          </span>}
                      </div>
                    </li>)}
                </ul>
                {plan.additionalNote && <p className="text-xs text-gray-400 mt-4 ml-8" data-id="element-3728">
                    {plan.additionalNote}
                  </p>}
              </div>
            </div>)}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white" data-id="element-3729">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3730">
          <div className="text-center mb-12" data-id="element-3731">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3732">
              Compare Plans
            </h2>
            <p className="text-xl text-gray-600" data-id="element-3733">
              Find the perfect plan for your team
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl shadow-lg" data-id="element-3734">
            <table className="w-full bg-white" data-id="element-3735">
              <thead data-id="element-3736">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-3737">
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wide" data-id="element-3738">
                    Key Features
                  </th>
                  <th className="px-6 py-4 text-center" data-id="element-3739">
                    <span className="text-lg font-bold text-gray-900" data-id="element-3740">
                      Starter
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center bg-blue-50" data-id="element-3741">
                    <span className="text-lg font-bold text-blue-600" data-id="element-3742">
                      Professional
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center" data-id="element-3743">
                    <span className="text-lg font-bold text-gray-900" data-id="element-3744">
                      Enterprise
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-3745">
                {comparisonFeatures.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-3746">
                    <td className="px-6 py-4 text-sm font-medium text-gray-700" data-id="element-3747">
                      {row.feature}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600" data-id="element-3748">
                      {typeof row.starter === 'boolean' ? row.starter ? <span className="text-green-500" data-id="element-3749">✓</span> : <span className="text-gray-300" data-id="element-3750">—</span> : row.starter}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium bg-blue-50" data-id="element-3751">
                      {typeof row.professional === 'boolean' ? row.professional ? <span className="text-green-500" data-id="element-3752">✓</span> : <span className="text-gray-300" data-id="element-3753">—</span> : row.professional}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600" data-id="element-3754">
                      {typeof row.enterprise === 'boolean' ? row.enterprise ? <span className="text-green-500" data-id="element-3755">✓</span> : <span className="text-gray-300" data-id="element-3756">—</span> : row.enterprise}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Recommendation Section */}
      <section className="py-16 bg-gray-50" data-id="element-3757">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3758">
          <div className="bg-white rounded-3xl p-10 shadow-lg" data-id="element-3759">
            <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3760">
              Not sure which plan to choose?
            </h3>
            <p className="text-gray-600 mb-8" data-id="element-3761">
              AI automation doesn't have to start everywhere at once. We
              recommend:
            </p>

            <div className="space-y-4 mb-8" data-id="element-3762">
              {[{
              title: 'Starter',
              description: 'For safe initial ROI and proof of concept. Test the waters before diving in.',
              color: 'green'
            }, {
              title: 'Professional',
              description: "After you've seen stable automation with humans in the loop. Ready to scale.",
              color: 'blue'
            }, {
              title: 'Enterprise',
              description: 'When automation is mission-critical and governed at scale across your organization.',
              color: 'purple'
            }].map((rec, index) => <div key={index} className="flex gap-4 p-5 bg-gray-50 rounded-2xl" data-id="element-3763">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${rec.color === 'green' ? 'bg-green-100 text-green-600' : rec.color === 'blue' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`} data-id="element-3764">
                    <CheckIcon className="h-5 w-5" data-id="element-3765" />
                  </div>
                  <div data-id="element-3766">
                    <h4 className="font-bold text-gray-900 mb-1" data-id="element-3767">
                      {rec.title}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-3768">{rec.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="bg-green-50 rounded-2xl p-4 text-center" data-id="element-3769">
              <p className="text-sm text-green-800 font-medium" data-id="element-3770">
                ✨ You can upgrade at any time — confidence grows with usage,
                not pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-id="element-3771">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3772">
          <div className="text-center mb-12" data-id="element-3773">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3774">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600" data-id="element-3775">
              Everything you need to know about ShoPilots pricing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-3776">
            {faqs.map((faq, index) => <div key={index} className="bg-gray-50 rounded-2xl p-6" data-id="element-3777">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3" data-id="element-3778">
                  <HelpCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0" data-id="element-3779" />
                  {faq.question}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3780">
                  {faq.answer}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900 text-white" data-id="element-3781">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3782">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-3783">
            Ready to transform your customer experience?
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-3784">
            Join 1,500+ businesses already growing with ShoPilots
          </p>
          <div className="flex gap-4 justify-center flex-wrap" data-id="element-3785">
            <a href="https://chat.shopilots.com/signup?product=shopilots" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-3786">
              Start Free Trial
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-3787" />
            </a>
            <a href="mailto:info@shopilots.com?subject=I%20want%20to%20talk%20to%20sales" className="inline-flex items-center px-8 py-4 bg-transparent text-white rounded-xl font-semibold border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all duration-300" data-id="element-3788">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>
    </div>;
};
export default NewPricingPage;